import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Users, Mail, Phone, MapPin, Edit, Trash2, Save, X, Lock, Loader2, Search, LogIn, Link2, Tag } from 'lucide-react';
import { axiosInstance } from '../App';
import { useNavigate } from 'react-router-dom';

// Admin page: see + edit registered klantenportaal users (db.client_users)
// Routes used: GET /admin/clients, PUT /admin/clients/{id}, DELETE /admin/clients/{id},
//              GET /admin/available-client-labels, POST /admin/clients/{id}/impersonate

const KlantenportaalUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [labelSearch, setLabelSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [r, lbl] = await Promise.all([
        axiosInstance.get('/admin/clients'),
        axiosInstance.get('/admin/available-client-labels').catch(() => ({ data: { labels: [] } })),
      ]);
      setUsers(r.data || []);
      setAvailableLabels((lbl.data && lbl.data.labels) || []);
    } catch (e) {
      toast.error('Kon klanten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const startEdit = (u) => {
    setEditingId(u.id);
    setLabelSearch('');
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      address: u.address || '',
      city: u.city || '',
      company_name: u.company_name || '',
      notes: u.notes || '',
      new_password: '',
      linked_addresses: Array.isArray(u.linked_addresses) ? [...u.linked_addresses] : [],
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); setLabelSearch(''); };

  const toggleLinkedLabel = (label) => {
    setEditForm((f) => {
      const cur = Array.isArray(f.linked_addresses) ? f.linked_addresses : [];
      const has = cur.some((x) => x.toLowerCase() === label.toLowerCase());
      return {
        ...f,
        linked_addresses: has
          ? cur.filter((x) => x.toLowerCase() !== label.toLowerCase())
          : [...cur, label],
      };
    });
  };

  const addCustomLabel = () => {
    const v = (labelSearch || '').trim();
    if (!v) return;
    setEditForm((f) => {
      const cur = Array.isArray(f.linked_addresses) ? f.linked_addresses : [];
      if (cur.some((x) => x.toLowerCase() === v.toLowerCase())) return f;
      return { ...f, linked_addresses: [...cur, v] };
    });
    setLabelSearch('');
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.new_password) delete payload.new_password;
      if (!Array.isArray(payload.linked_addresses)) payload.linked_addresses = [];
      await axiosInstance.put(`/admin/clients/${id}`, payload);
      toast.success('Klant bijgewerkt');
      setEditingId(null);
      setLabelSearch('');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Klantaccount "${u.name || u.email}" verwijderen?\n\nDe klant kan niet meer inloggen, maar offertes/facturen blijven bestaan.`)) return;
    try {
      await axiosInstance.delete(`/admin/clients/${u.id}`);
      toast.success('Klant verwijderd');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const handleImpersonate = async (u) => {
    if (!window.confirm(`Inloggen als "${u.name || u.email}"?\n\nU ziet alles wat de klant ziet. Klik op uitloggen om terug te keren naar uw eigen account.`)) return;
    try {
      const r = await axiosInstance.post(`/admin/clients/${u.id}/impersonate`);
      // Save client token + data exactly like ClientLogin.jsx does
      localStorage.setItem('clientToken', r.data.access_token);
      localStorage.setItem('clientData', JSON.stringify(r.data.client));
      // Keep admin token in a separate key so admin can return
      const adminToken = localStorage.getItem('token');
      if (adminToken) localStorage.setItem('admin_return_token', adminToken);
      toast.success(`Ingelogd als ${u.name || u.email}`);
      navigate('/client/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Inloggen mislukt');
    }
  };

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [u.name, u.email, u.phone, u.address, u.city, u.company_name]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6" data-testid="klantenportaal-users-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Klantenportaal — gebruikers</h1>
        <p className="text-sm text-gray-500 mt-1">Beheer de klanten die ingelogd kunnen worden op het klantenportaal.</p>
      </div>

      <Card className="bg-gradient-to-r from-[#3da5a5]/10 to-white border-[#3da5a5]/30">
        <CardContent className="py-4 flex items-center gap-4">
          <Users className="w-10 h-10 text-[#3da5a5]" />
          <div>
            <p className="text-[11px] uppercase text-gray-500">Totaal accounts</p>
            <p className="text-2xl font-bold text-gray-800" data-testid="users-count">{users.length}</p>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op naam, e-mail, adres..."
          className="pl-9 bg-white"
          data-testid="users-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-500">Geen klanten gevonden.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((u) => (
            <Card key={u.id} className="hover:shadow-md transition-shadow" data-testid={`user-card-${u.id}`}>
              {editingId === u.id ? (
                <CardContent className="py-4 space-y-3 bg-amber-50/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Naam</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid={`edit-name-${u.id}`} /></div>
                    <div><Label className="text-xs">E-mail</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid={`edit-email-${u.id}`} /></div>
                    <div><Label className="text-xs">Telefoon</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                    <div><Label className="text-xs">Bedrijfsnaam (optioneel)</Label><Input value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">Adres</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
                    <div><Label className="text-xs">Plaats</Label><Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                    <div>
                      <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Nieuw wachtwoord <span className="text-gray-400">(optioneel)</span></Label>
                      <Input type="password" value={editForm.new_password} onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })} placeholder="Laat leeg om niet te wijzigen" data-testid={`edit-password-${u.id}`} />
                    </div>
                    <div className="md:col-span-2"><Label className="text-xs">Interne notities (alleen admin)</Label><textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="w-full bg-white border border-gray-200 rounded-lg p-2 text-sm h-16" /></div>
                  </div>

                  {/* Linked client labels — admin maps which CRM addresses/client names this portal user can see */}
                  <div className="border-t border-amber-200 pt-3 mt-2">
                    <Label className="text-xs flex items-center gap-1.5 mb-1.5 text-gray-700">
                      <Link2 className="w-3.5 h-3.5 text-[#3da5a5]" />
                      Gekoppelde klantnamen / adres-labels
                      <span className="text-gray-400 font-normal">(deze klant ziet alle offertes/facturen die met onderstaande namen zijn aangemaakt)</span>
                    </Label>

                    {/* Selected chips */}
                    {Array.isArray(editForm.linked_addresses) && editForm.linked_addresses.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {editForm.linked_addresses.map((l) => (
                          <span key={l} className="inline-flex items-center gap-1 bg-[#3da5a5] text-white text-xs rounded-full pl-2.5 pr-1.5 py-1" data-testid={`linked-chip-${l}`}>
                            <Tag className="w-3 h-3" /> {l}
                            <button type="button" onClick={() => toggleLinkedLabel(l)} className="ml-1 bg-white/20 hover:bg-white/40 rounded-full w-4 h-4 inline-flex items-center justify-center" title="Verwijderen">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Search + add */}
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={labelSearch}
                        onChange={(e) => setLabelSearch(e.target.value)}
                        placeholder="Zoek bestaande klant of typ een nieuwe..."
                        className="bg-white text-sm h-9"
                        data-testid={`label-search-${u.id}`}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLabel(); } }}
                      />
                      <Button type="button" size="sm" variant="outline" onClick={addCustomLabel} disabled={!labelSearch.trim()} data-testid={`add-custom-label-${u.id}`}>
                        + Toevoegen
                      </Button>
                    </div>

                    {/* Suggestion list (filtered) */}
                    {(() => {
                      const q = labelSearch.trim().toLowerCase();
                      const linked = (editForm.linked_addresses || []).map((x) => x.toLowerCase());
                      const candidates = availableLabels
                        .filter((l) => !linked.includes(l.toLowerCase()))
                        .filter((l) => !q || l.toLowerCase().includes(q))
                        .slice(0, 12);
                      if (candidates.length === 0) return (
                        <p className="text-[11px] text-gray-400 italic">{q ? 'Geen suggesties — gebruik "Toevoegen" om handmatig te koppelen.' : 'Alle labels zijn al gekoppeld of geen labels beschikbaar.'}</p>
                      );
                      return (
                        <div className="flex flex-wrap gap-1.5" data-testid={`label-suggestions-${u.id}`}>
                          {candidates.map((l) => (
                            <button
                              key={l}
                              type="button"
                              onClick={() => toggleLinkedLabel(l)}
                              className="inline-flex items-center gap-1 bg-white hover:bg-[#3da5a5]/10 border border-gray-300 hover:border-[#3da5a5] text-xs rounded-full px-2.5 py-1 text-gray-700 transition-colors"
                              data-testid={`suggest-label-${l}`}
                            >
                              + {l}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-amber-200">
                    <Button onClick={() => saveEdit(u.id)} disabled={saving} className="bg-[#3da5a5] hover:bg-[#2d8585] text-white" data-testid={`save-user-${u.id}`}>
                      {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Opslaan...</> : <><Save className="w-4 h-4 mr-1" /> Opslaan</>}
                    </Button>
                    <Button onClick={cancelEdit} variant="outline" data-testid={`cancel-edit-${u.id}`}><X className="w-4 h-4 mr-1" /> Annuleren</Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="py-4">
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-800 truncate">{u.name || '(geen naam)'}</h3>
                      {u.company_name && <p className="text-xs text-gray-500">{u.company_name}</p>}
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-gray-600">
                        {u.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#3da5a5]" /> {u.email}</div>}
                        {u.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#3da5a5]" /> {u.phone}</div>}
                        {(u.address || u.city) && <div className="flex items-center gap-1.5 md:col-span-2"><MapPin className="w-3.5 h-3.5 text-[#3da5a5]" /> {[u.address, u.city].filter(Boolean).join(', ')}</div>}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">
                        Geregistreerd: {u.created_at ? new Date(u.created_at).toLocaleString('nl-NL') : '-'}
                        {u.last_login_at && <span> · Laatst ingelogd: {new Date(u.last_login_at).toLocaleString('nl-NL')}</span>}
                      </p>
                      {Array.isArray(u.linked_addresses) && u.linked_addresses.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid={`user-linked-${u.id}`}>
                          <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Gekoppeld:</span>
                          {u.linked_addresses.map((l) => (
                            <span key={l} className="text-[11px] bg-[#3da5a5]/10 text-[#1a5276] border border-[#3da5a5]/30 rounded-full px-2 py-0.5">
                              {l}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => handleImpersonate(u)} className="text-purple-700 border-purple-300 hover:bg-purple-50" data-testid={`impersonate-user-${u.id}`} title="Inloggen als deze klant — u ziet exact wat de klant ziet">
                        <LogIn className="w-4 h-4 mr-1" /> Inloggen
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(u)} className="text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4 mr-1" /> Bewerken</Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(u)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default KlantenportaalUsers;
